import type { SiteConfig } from "../../api/types";
import { formatBalance, formatCount } from "../../lib/format";
import { CLAIM_GAS, INDEXSY_MINT } from "../../lib/facts";
import { Link } from "../../router/router";
import type { FaqItem } from "./faqTypes";

const LP_WALLET = "5Q544fKrFoe6tsEbD7S8EmxGTJYAKtTVhAW5Q5pge4j1";

/** Questions about who gets the airdrop, when, and how to spot a fake. */
export function faqAirdrop(config: SiteConfig | null): FaqItem[] {
  const eligible = config ? formatCount(config.snapshot.eligibleWallets) : "200";
  const cutoff = config ? formatBalance(config.snapshot.cutoffBalance) : null;
  const takenAt = config?.snapshot.takenAt ?? null;

  return [
    {
      id: "eligibility",
      question: "Who is eligible?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            The top 200 $INDEXSY holders on Solana at the snapshot. That is {eligible} wallets.
            {cutoff ? ` The smallest eligible balance was ${cutoff} $INDEXSY.` : ""}{" "}
            {takenAt
              ? `The snapshot was taken on ${takenAt}, before any announcement.`
              : "The snapshot was taken before any announcement, and its date is published before registration opens."}
          </p>
          <p className="body">
            $INDEXSY is a Solana token and its mint is{" "}
            <span className="mono">{INDEXSY_MINT}</span>. Two impostor tokens use the same ticker,
            so check the mint before you compare balances.
          </p>
          <p className="body">
            The liquidity pool wallet was removed before the ranking, so it does not take a share.
            That wallet is <span className="mono">{LP_WALLET}</span>.
          </p>
          <p className="body">
            The list is fixed. Buying $INDEXSY now does not add you, and moving your $INDEXSY does
            not remove you. Check any address on the <Link to="/airdrop">airdrop page</Link>.
          </p>
        </div>
      ),
    },
    {
      id: "cost",
      question: "What does the airdrop cost me?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            The airdrop is free. There is nothing to do except prove the wallet with Phantom and
            register an address with MetaMask. Claiming sends one transaction, which costs you{" "}
            {CLAIM_GAS} in gas on Robinhood Chain.
          </p>
          <p className="body">
            There are no tasks, no posting, and no referrals, ever. Nothing on this site asks you to
            send tokens anywhere.
          </p>
        </div>
      ),
    },
    {
      id: "allocation",
      question: "How is my allocation worked out?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            The team funds a pool of $TISM out of the developer buy made at launch. Each eligible
            wallet gets a share of that pool.
          </p>
          <p className="body">
            {config?.pool.mode === "cap5"
              ? "Shares are pro rata by snapshot balance, with a 5% cap per wallet so the largest holders do not take most of the pool."
              : "Shares are pro rata by snapshot balance, so a wallet holding twice as much gets twice as much."}
          </p>
          <p className="body">
            Until the pool is funded the site shows your share as a percentage and leaves the token
            amount blank. It fills in on its own once the pool size is set.
          </p>
        </div>
      ),
    },
    {
      id: "timing",
      question: "When does this happen?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            There are three phases. Registration is when eligible wallets link a Solana address to
            an EVM address. Then the Merkle tree is built from the registrations and the distributor
            is funded. Then claims open.
          </p>
          <p className="body">
            The airdrop page always shows the current phase, and the deadline while registration is
            open. Those come from the server, not from this text.
          </p>
          <p className="body">
            If you miss registration you are not in the tree, and the tree cannot be changed after
            it is published.
          </p>
        </div>
      ),
    },
    {
      id: "safety",
      question: "How do I know a message about $TISM is fake?",
      answer: (
        <div className="stack stack-3">
          <p className="body">
            Nobody from this project will DM you first. Not on X, not on Telegram, not on Discord,
            not anywhere else. This site is the only official channel, so if someone messages you
            first claiming to be from $TISM, it is not us.
          </p>
          <p className="body">
            Nobody will ever ask for your seed phrase or your private key. There is no support agent
            who needs it, and no situation where handing it over helps you.
          </p>
          <p className="body">
            Claiming only ever happens on this site, and it only ever asks you to sign a message and
            send one claim transaction. It never asks you to send tokens anywhere.
          </p>
        </div>
      ),
    },
  ];
}
